import React from "react";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as SupportIcon } from "@assets/Icons/IconSupport.svg";
import { MaintenanceWindow } from "@redux/slices/maintenanceSlice";

interface MaintenanceBlockProps {
  window: MaintenanceWindow;
}

/**
 * Replaces the telemetry-backed views while a `blocked` window is open.
 *
 * The case for a full stop rather than the notice: when there is genuinely
 * nothing to render, a dashboard full of dashes and grey cards invites the
 * support ticket it was meant to prevent — the customer reads it as
 * something being wrong with their services rather than with our view of
 * them. One screen that says so plainly is kinder and quieter.
 *
 * Two things it must keep doing:
 *
 *  - Lead with the reassurance. The first sentence a worried customer reads
 *    has to be that their services are running, because that is both true and
 *    the thing they came to check.
 *  - Leave Support reachable, here and in the side menu. A dark dashboard
 *    with no way to reach a person is how a maintenance window turns into a
 *    phone call to an account manager.
 */
const MaintenanceBlock: React.FC<MaintenanceBlockProps> = ({ window }) => {
  const navigate = useNavigate();

  const until = new Date(window.until);
  const untilLabel = Number.isNaN(until.getTime())
    ? null
    : until.toLocaleString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        day: "numeric",
        month: "short",
      });

  return (
    <div className="flex min-h-[calc(100vh-14rem)] items-center justify-center px-4">
      <div className="w-full max-w-xl">
        <GenericCard
          isCollapsible={false}
          bodyContent={
            <div className="flex flex-col items-center gap-5 p-8 text-center">
              <div className="inline-flex items-center justify-center rounded-md border border-[var(--color-utility-warning-600)] bg-semantic-bg-warning-secondary p-3 text-semantic-text-warning">
                <svg
                  className="h-10 w-10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-bold text-semantic-text-primary">
                  Your services are running — monitoring is paused
                </h2>
                <p className="text-sm leading-relaxed text-semantic-text-secondary">
                  {window.message}
                </p>
                {untilLabel && (
                  <p className="text-sm font-medium text-semantic-text-primary">
                    Expected to end at {untilLabel}.
                  </p>
                )}
              </div>

              <div className="w-full border-t border-semantic-border-primary pt-5">
                <p className="mb-3 text-sm text-semantic-text-secondary">
                  Need something in the meantime? Our team is available as
                  usual.
                </p>
                <GenericButton
                  label="Contact support"
                  variant="brand"
                  size="md"
                  icon={<SupportIcon className="h-4 w-4 text-white" />}
                  className="justify-center"
                  onClick={() => navigate("/support/request")}
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MaintenanceBlock;
